import { useState } from 'react';
import { Button, Col, Row, Skeleton, Tabs, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FaRegFilePdf } from 'react-icons/fa';
import {
  GetInternshipApplicationsById,
  GetInternshipProcessesOfStudent,
  RollbackProcessStep,
} from '../../helper/apiHelper';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { StepStatus } from '../../Types/interfaces';
import type {
  CombinedInternshipApplication,
  InternshipProcess,
  ProcessRollback,
  ProcessSteps,
  RollbackStudent,
  StepName,
} from '../../Types/interfaces';
import { NoData } from '../NoData';
import {
  ORGANIZATION_STEPS,
  UNIVERSITY_STEPS,
  type ProcessStepDefinition,
} from './processStepDefinitions';
import {
  RollbackStepList,
  type RollbackStepItem,
  type RollbackStepState,
} from './RollbackStepList';
import { RollbackConfirmPanel } from './RollbackConfirmPanel';
import { RollbackHistoryTable } from './RollbackHistoryTable';

interface RollbackProcessViewerProps {
  student: RollbackStudent;
}

interface ConfirmingRollback {
  processId: number;
  stepName: StepName;
  stepTitle: string;
}

interface RollbackMutationVariables {
  processId: number;
  rollback: ProcessRollback;
  // only for the success message, it is not sent to the backend
  stepTitle: string;
}

// "Accepting internship by organization" is not a process step, it can't be rolled back
const ACCEPTING_KEY = 'accepting';

// in a signing chain the last signer's step is only uploaded, the previous one is
// finished, both count as done
const isDone = (step: ProcessSteps | undefined): boolean =>
  !!step &&
  !step.disabled &&
  (step.stepStatus === StepStatus.updated ||
    step.stepStatus === StepStatus.finished);

const getStepState = (step: ProcessSteps | undefined): RollbackStepState => {
  if (isDone(step)) return 'done';
  if (step && !step.disabled && step.stepStatus === StepStatus.initialized) {
    return 'current';
  }
  return 'upcoming';
};

const getApplicationState = (
  application: CombinedInternshipApplication
): RollbackStepState => {
  switch (application.status) {
    case 0:
      return 'current';
    case 1:
      return 'done';
    default:
      return 'upcoming';
  }
};

// the last done step of a column can be rolled back, if no step depending on it is done
const getRollbackStepName = (
  definitions: ProcessStepDefinition[],
  processSteps: ProcessSteps[]
): StepName | undefined => {
  const findStep = (stepName: StepName) =>
    processSteps.find((s) => s.stepName === stepName);
  const lastDone = [...definitions]
    .reverse()
    .find((definition) => isDone(findStep(definition.stepName)));
  if (!lastDone?.canRollback) return undefined;
  if (lastDone.blockedBy?.some((stepName) => isDone(findStep(stepName)))) {
    return undefined;
  }
  return lastDone.stepName;
};

export const RollbackProcessViewer = ({
  student,
}: RollbackProcessViewerProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const [confirming, setConfirming] = useState<ConfirmingRollback>();

  const { data: items = [], isLoading } = useQuery({
    queryKey: [
      ...QUERY_KEYS.PROCESS_ROLLBACK_STUDENT_PROCESSES,
      student.id,
      student.processIds,
    ],
    queryFn: async () => {
      // the endpoint returns the processes of every study year,
      // keep only the ones of the selected study year
      const processes = (
        await GetInternshipProcessesOfStudent(student.id)
      ).filter((process) => student.processIds.includes(process.id));
      // the offer name for the tab label is only on the application
      return Promise.all(
        processes.map(async (process) => ({
          process,
          application: await GetInternshipApplicationsById(
            process.internshipApplicationId
          ),
        }))
      );
    },
  });

  const rollbackMutation = useMutation<void, Error, RollbackMutationVariables>({
    mutationFn: (variables: RollbackMutationVariables): Promise<void> =>
      RollbackProcessStep(variables.processId, variables.rollback),
    onSuccess: (data: void, variables: RollbackMutationVariables): void => {
      messageApi.success(
        t('processRollback.rolledBack', { step: variables.stepTitle })
      );
      setConfirming(undefined);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROCESS_ROLLBACK_STUDENT_PROCESSES,
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.PROCESS_ROLLBACK_HISTORY, variables.processId],
      });
    },
    onError: (): void => {
      messageApi.error(t('processRollback.rollbackError'));
    },
  });

  const toStepItems = (
    definitions: ProcessStepDefinition[],
    process: InternshipProcess
  ): RollbackStepItem[] => {
    const rollbackStepName = getRollbackStepName(
      definitions,
      process.processSteps
    );
    return (
      definitions
        .map((definition) => ({
          definition,
          step: process.processSteps.find(
            (s) => s.stepName === definition.stepName
          ),
        }))
        // e.g. work safety only exists for university organizations
        .filter(({ step }) => !!step)
        .map(({ definition, step }) => ({
          key: definition.stepName,
          title: t(definition.titleKey),
          state: getStepState(step),
          disabled: step?.disabled,
          canRollback: definition.stepName === rollbackStepName,
        }))
    );
  };

  const renderStepList = (
    title: string,
    process: InternshipProcess,
    stepItems: RollbackStepItem[],
    footer?: React.ReactNode
  ) => {
    const confirmingThisProcess =
      confirming?.processId === process.id ? confirming : undefined;
    return (
      <RollbackStepList
        title={title}
        items={stepItems}
        confirmingKey={confirmingThisProcess?.stepName}
        onRollback={(item) =>
          setConfirming({
            processId: process.id,
            stepName: item.key as StepName,
            stepTitle: item.title,
          })
        }
        confirmPanel={
          confirmingThisProcess && (
            // a new panel (empty reason) for every step
            <RollbackConfirmPanel
              key={confirmingThisProcess.stepName}
              stepTitle={confirmingThisProcess.stepTitle}
              resetsSigning={[...ORGANIZATION_STEPS, ...UNIVERSITY_STEPS].some(
                (definition: ProcessStepDefinition): boolean =>
                  definition.stepName === confirmingThisProcess.stepName &&
                  definition.resetsSigning === true
              )}
              loading={rollbackMutation.isPending}
              onCancel={() => setConfirming(undefined)}
              onConfirm={(reason: string, notify: boolean): void =>
                rollbackMutation.mutate({
                  processId: confirmingThisProcess.processId,
                  rollback: {
                    stepName: confirmingThisProcess.stepName,
                    reason: reason,
                    notify: notify,
                  },
                  stepTitle: confirmingThisProcess.stepTitle,
                })
              }
            />
          )
        }
        footer={footer}
      />
    );
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  if (items.length === 0) {
    return <NoData message={t('processRollback.noProcesses')} />;
  }

  return (
    <>
      {contextHolder}
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
        {student.lastName} {student.firstName}
      </Typography.Title>
      <Typography.Text type="secondary">{student.email}</Typography.Text>

      <Tabs
        centered
        type="card"
        tabBarGutter={16}
        style={{ marginTop: 16 }}
        onChange={() => setConfirming(undefined)}
        items={items.map(({ process, application }) => ({
          key: String(process.id),
          label: `${application.internshipOfferName} | ${process.organizationName}`,
          children: (
            <>
              <Typography.Text strong>
                {t('internshipProcessComp.organization')}:{' '}
                {process.organizationName}
              </Typography.Text>
              <br />
              <Typography.Text strong>
                {t('internshipProcessComp.status')}:{' '}
                {application.status === 1
                  ? t('internshipProcessComp.accepted')
                  : t('internshipProcessComp.pending')}
              </Typography.Text>

              <Row gutter={[40, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} xl={12}>
                  {renderStepList(
                    t('internshipProcessComp.orgProcesses'),
                    process,
                    [
                      {
                        key: ACCEPTING_KEY,
                        title: t('internshipProcessComp.accepting'),
                        state: getApplicationState(application),
                      },
                      ...toStepItems(ORGANIZATION_STEPS, process),
                    ]
                  )}
                </Col>
                <Col xs={24} xl={12}>
                  {renderStepList(
                    t('internshipProcessComp.universityProcesses'),
                    process,
                    toStepItems(UNIVERSITY_STEPS, process),
                    !!process.conventionDocumentUrl && (
                      <Row justify="center" style={{ paddingTop: 24 }}>
                        <Button
                          icon={<FaRegFilePdf />}
                          href={`${process.conventionDocumentUrl}?status=${process.currentUniversityStepName ?? ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('internshipProcessComp.viewConvention')}
                        </Button>
                      </Row>
                    )
                  )}
                </Col>
              </Row>
              <RollbackHistoryTable processId={process.id} />
            </>
          ),
        }))}
      />
    </>
  );
};
